import {useContext, useMemo} from 'react';

import PersonIcon from '@mui/icons-material/Person';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import VerifiedIcon from '@mui/icons-material/Verified';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SchoolIcon from '@mui/icons-material/School';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import ConstructionIcon from '@mui/icons-material/Construction';
import ChecklistIcon from '@mui/icons-material/Checklist';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ForestIcon from '@mui/icons-material/Forest';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/History';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';

import {FirefighterRanks} from '@embertracking/common';

import {UserContext} from '-/contexts/User';
import {SettingsContext} from '-/contexts/Settings';
import {hasPermission} from '-/data/utils';
import {isTrackingEnabled} from '-/data/driverHours';
import {hasFeature} from '-/features';

export const MEMBER_GROUPS = [
    {id: 'member', label: 'Member'},
    {id: 'training', label: 'Training'},
    {id: 'activity', label: 'Activity'},
    {id: 'admin', label: 'Administration'}
];

// The one source of truth for the member rail: nav order, labels, icons and
// gating. This replaces the run of imperative `tabs.push(...)` calls the tab bar
// used, where every gate had to be written twice — once to decide whether the
// tab appeared, once at render to decide whether the panel did (the route can
// always be hit directly, so both are needed).
//
// `gate` receives {currentUser, isProfile, isNew, archived, usesJIBC, role,
// hasFeature} rather than reaching for module state, so the matrix is
// unit-testable without mocking features.js.
export const MEMBER_SECTIONS = [
    {
        id: 'edit',
        label: 'Profile',
        group: 'member',
        icon: PersonIcon
    },
    {
        id: 'serviceHistory',
        label: 'Service History',
        group: 'member',
        icon: MilitaryTechIcon,
        // #1718 — rank + status history as one timeline. Visible to the member on
        // their own profile (it's their service record) and to anyone who can
        // already read the member; the panel itself renders read-only without
        // users.write, so no permission gate is needed here beyond `!isNew` —
        // there's no member yet to have a history.
        //
        // Only for accounts on the FIREFIGHTER ladder. The other `Ranks` entries
        // (Apparatus, Employee, Administrator) are account kinds, not rungs —
        // there is no promotion path through them, so a service record showing
        // "time in rank: Administrator" describes nothing. A blank role counts as
        // off-ladder for the same reason: there's no rank to have a history of.
        gate: ({isNew, role}) => !isNew && FirefighterRanks.includes(role),
        // 'acting'/'interim' are both here because departments use both words for
        // the same thing, and the feature is only findable by the word you know.
        keywords: ['rank', 'promotion', 'service', 'history', 'seniority', 'time in rank', 'acting', 'interim']
    },
    {
        id: 'records',
        label: 'Records',
        group: 'member',
        icon: FolderSharedIcon,
        // HR records (#1482). Members see it on their own profile — the api
        // filters confidential records out of a self-view — while viewing
        // someone else's needs the dedicated hr.read claim (users.write is
        // deliberately not enough). `features.hr` defaults on (#1568); a
        // department can switch the module off in Settings → Features.
        gate: ({currentUser, isProfile, isNew, hasFeature}) =>
            !isNew && hasFeature('hr') && (isProfile || hasPermission(currentUser, 'hr.read'))
    },
    {
        id: 'qualifications',
        label: 'Qualifications',
        group: 'training',
        icon: VerifiedIcon,
        gate: ({isNew}) => !isNew
    },
    {
        id: 'assignments',
        label: 'Assignments',
        group: 'training',
        icon: AssignmentTurnedInIcon,
        // Requirement assignments for this member. On your own profile you always
        // see it; for another member it needs requirement report/read so the
        // evidence downloads work.
        gate: ({currentUser, isProfile, isNew, hasFeature}) =>
            !isNew && hasFeature('requirements') &&
            (isProfile || hasPermission(currentUser, 'requirements.read') || hasPermission(currentUser, 'requirements.report'))
    },
    {
        id: 'driverClearance',
        label: 'Driver Clearance',
        group: 'training',
        icon: DirectionsCarIcon,
        // Driver training hours (#1580). The member-facing half of the feature: gated exactly
        // like `assignments` above, so a member always sees their own and no permission is
        // needed to log hours against themselves. Viewing someone else's needs a requirements
        // claim.
        //
        // Gated on the department having OPTED IN, not on it being fully configured — the
        // narrower "ready" test needs the apparatus list, which this hook has no business
        // reaching for. A department that ticks the box and hasn't typed a target yet shows
        // the tab, and the panel says there are no targets. Officers get the sharper
        // distinction (see driverHoursSetupState); a member can't act on either state.
        gate: ({currentUser, isProfile, isNew, driverTrainingHours}) =>
            !isNew && driverTrainingHours &&
            (isProfile || hasPermission(currentUser, 'requirements.read') || hasPermission(currentUser, 'requirements.report')),
        keywords: ['driver', 'driving', 'hours', 'apparatus', 'cleared to drive']
    },
    {
        id: 'transcript',
        label: 'JIBC Transcript',
        group: 'training',
        icon: SchoolIcon,
        gate: ({usesJIBC}) => usesJIBC
    },
    {
        id: 'jibcConsent',
        label: 'JIBC Consent',
        group: 'training',
        icon: HowToRegIcon,
        gate: ({currentUser, usesJIBC}) => usesJIBC && hasPermission(currentUser, 'users.write')
    },
    {
        id: 'incidents',
        label: 'Incidents',
        group: 'activity',
        icon: WhatshotIcon,
        gate: ({currentUser}) => hasPermission(currentUser, 'incidents.read')
    },
    {
        // The member's own attendance record, so it sits with the other things
        // they turned up to rather than with the curriculum sections above.
        id: 'training',
        label: 'Training',
        group: 'activity',
        icon: FactCheckIcon,
        gate: ({currentUser}) => hasPermission(currentUser, 'training.read')
    },
    {
        id: 'deployments',
        label: 'Deployments',
        group: 'activity',
        icon: ForestIcon,
        // The member's own wildland/mutual-aid record. On your own profile you
        // always see it — a firefighter's `deployments.read` is off by default
        // (permissionRoles.js gives the ladder `ff: 0`), so gating this on it
        // would hide a member's own tour of duty from them. Viewing someone
        // else's needs the deployments module, same bar the api applies
        // (api/src/routes/memberDeployments.js). `!isNew`: there's no member yet
        // to have been anywhere.
        gate: ({currentUser, isProfile, isNew, hasFeature}) =>
            !isNew && hasFeature('deployments') &&
            (isProfile || hasPermission(currentUser, 'deployments.read')),
        keywords: ['deployment', 'wildfire', 'wildland', 'bcws', 'mutual aid', 'rotation', 'strike team']
    },
    {
        id: 'gear',
        label: 'Gear',
        group: 'activity',
        icon: ConstructionIcon,
        gate: ({currentUser, isProfile}) => isProfile || hasPermission(currentUser, 'gear.read')
    },
    {
        id: 'tasks',
        label: 'Tasks',
        group: 'activity',
        icon: ChecklistIcon,
        gate: ({currentUser, archived}) => !archived && hasPermission(currentUser, 'tasks.write')
    },
    {
        id: 'permissions',
        label: 'Permissions',
        group: 'admin',
        icon: LockIcon,
        gate: ({currentUser, isProfile, archived}) => !isProfile && !archived && hasPermission(currentUser, 'admin')
    },
    {
        id: 'history',
        label: 'History',
        group: 'admin',
        icon: HistoryIcon,
        gate: ({currentUser, isNew}) => !isNew && hasPermission(currentUser, 'users.write')
    }
];

// Filter by the caller-supplied gating context. Pure, so the matrix is testable
// without touching features.js's module-level store.
export const visibleMemberSections = ctx => MEMBER_SECTIONS.filter(section => !section.gate || section.gate(ctx));

// The section a member page falls back to — the profile form, which lives at the
// bare base path so existing links (/profile, /users/:id) keep working.
export const DEFAULT_MEMBER_SECTION = 'edit';

// `base` is '/profile' or `/users/${uid}`.
export const memberSectionPath = (base, id) => (id === DEFAULT_MEMBER_SECTION ? base : `${base}/${id}`);

export const memberBasePath = (isProfile, uid) => (isProfile ? '/profile' : `/users/${uid}`);

// SettingsContext backs hasFeature() and carries usesJIBC; depend on the context
// (not just the function) so toggling a feature re-renders the rail live — the
// same trick useMenuItems.js and settings/sections.js use.
export default function useMemberSections({isProfile = false, isNew = false, archived = false, role = null} = {}) {
    const {currentUser} = useContext(UserContext);
    const settings = useContext(SettingsContext);
    const {usesJIBC} = settings;
    // Driving hours (#1580) hang off the Requirements module AND their own opt-in toggle: a
    // department can run Requirements without a driver training program.
    const driverTrainingHours = hasFeature('requirements') && isTrackingEnabled(settings);

    return useMemo(
        () => visibleMemberSections({currentUser, isProfile, isNew, archived, usesJIBC, role, driverTrainingHours, hasFeature}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentUser, settings, isProfile, isNew, archived, usesJIBC, role, driverTrainingHours]
    );
}
